import React, {useEffect, useMemo} from 'react';
import {useLocation, useParams, useSearchParams} from 'react-router-dom';
import {Box} from '@tenancy.nz/ui';
import {pageView} from '../../analytics';
import BookingSuccess from '../../components/BookingSuccess';
import EnquirySuccess from '../../components/EnquirySuccess';
import Footer from '../../components/Footer';
import Nameplate from '../../components/Nameplate';
import NotFound from '../../components/NotFound';
import RemarketingFeature from './RemarketingFeature';
import {useInitAgencyBySlugQuery} from '../../api/main/agency';
import {useGetUpcomingViewingsByPropertyQuery} from '../../api/bookme/viewing';
import {env, isArray, toNumber} from '../../utils/helpers';
import * as consts from '../../utils/constants';

function BookingSuccessFeature() {
  const {id} = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const viewingParam = searchParams.get(consts.URL_PARAM_VIEWING);
  const isEnquiry = viewingParam === '0' || searchParams.has('enquiry');

  const {
    data: {prepared} = {prepared: {}},
    isError,
    isLoading,
  } = useInitAgencyBySlugQuery(id, {skip: !id});
  const {agency, property} = prepared;

  const {data: {prepared: viewings} = {prepared: []}} =
    useGetUpcomingViewingsByPropertyQuery(
      {
        id,
        options: {
          query: {
            [consts.API_PARAM_ATTENDEE_COUNT]: 1,
            [consts.API_PARAM_BOOKING_COUNT]: 1,
            [consts.API_PARAM_SORT]: 'date_time',
          },
        },
      },
      {skip: isEnquiry || !id || isLoading || isError}
    );

  const submittedViewing = useMemo(() => {
    if (isEnquiry || !isArray(viewings)) {
      return {date: '', time: ''};
    }
    const match = viewings.find(v => v.id === toNumber(viewingParam));
    const [date = '', time = ''] = (match?.localDateTime || '').split(' ');
    return {date, time};
  }, [isEnquiry, viewingParam, viewings]);

  useEffect(() => {
    pageView({path: location.pathname, title: document.title});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) {
    return <Nameplate skeleton />;
  }

  if (!agency?.hasBookMeAccess || isError) {
    return <NotFound />;
  }

  return (
    <React.Fragment>
      <Nameplate
        skeleton={isLoading}
        heading={agency.companyName}
        logo={agency.logo}
        ctaLabel="Manage your bookings"
        ctaHref={`${env('REACT_APP_TENANT_PORTAL_URL')}/viewings`}
      />
      <Box sx={{my: 6}}>
        {isEnquiry ? (
          <EnquirySuccess address={property?.address} />
        ) : (
          <BookingSuccess
            address={property?.address}
            viewingDate={submittedViewing.date}
            viewingTime={submittedViewing.time}
          />
        )}
      </Box>
      {agency?.remarketingEnabled && <RemarketingFeature />}
      <Footer />
    </React.Fragment>
  );
}

export default BookingSuccessFeature;
